import { apiClient } from '@applyonline/shared'
import {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  ComputePrequalPaymentRequestDto,
  ComputePrequalPaymentResultDto,
  MonthlyPayment
} from '@applyonline/shared/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'

export interface ComputeAmountForFrequencyDto extends BaseComputeAmountForFrequencyDto {
  merchantId: string
  paymentPlanId: string | null
  hasCoapplicant: boolean
  include84Months: boolean
}

const AMOUNT_FOR_FREQUENCY = 'amountForFrequency'
const AMOUNT_FOR_PROMOTIONAL_CREDIT_APP = 'amountForPromotionalCreditApp'
const PREQUAL_PAYMENT = 'prequalPayment'

const keysFactory = {
  amountForFrequency: (computeParams: ComputeAmountForFrequencyDto | null) =>
    [{ scope: AMOUNT_FOR_FREQUENCY }, computeParams] as const,
  amountForPromotionalCreditApp: (computeFunding: ComputeFundingDto) =>
    [{ scope: AMOUNT_FOR_PROMOTIONAL_CREDIT_APP }, computeFunding] as const,
  prequalPayment: (params: ComputePrequalPaymentRequestDto | null) =>
    [{ scope: PREQUAL_PAYMENT }, params] as const,
}

const computePaymentAmountForFrequency = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForFrequency']>>) => {
  const response = await apiClient.post('personal/CreditApplication/computeAmountForFrequency', computeParams)
  return response.data as ComputedAmountForFrequency
}

export function useComputePaymentAmountForFrequency(
  computeParams: ComputeAmountForFrequencyDto | null,
): [ComputedAmountForFrequency | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.amountForFrequency(computeParams)],
    queryFn: computePaymentAmountForFrequency,
    enabled: computeParams !== null,
  })

  return [data ?? null, isFetching]
}

const getPromotionalCreditAppPlan = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForPromotionalCreditApp']>>) => {
  const response = await apiClient.post<ComputeFundingResultDto>(
    `personal/CreditApplication/${encodeURIComponent(computeParams.creditApplicationId)}/computeFunding`,
    computeParams,
  )
  return response.data
}

export function useGetPromotionalCreditAppPlan(
  amountRequested: number,
  creditApplicationId: string,
  paymentPlanId: string | null,
): [ComputeFundingResultDto | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.amountForPromotionalCreditApp({
      amountRequested,
      creditApplicationId,
      paymentPlanId,
    } as ComputeFundingDto),
    queryFn: getPromotionalCreditAppPlan,
    enabled: !!creditApplicationId && Boolean(paymentPlanId),
  })

  return [data ?? null, isFetching]
}

const getPrequalPayment = async ({
  queryKey: [_, params],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['prequalPayment']>>) => {
  const response = await apiClient.post<ComputePrequalPaymentResultDto>(
    'personal/CreditApplication/ComputeMonthlyPayment',
    params,
  )
  return response.data
}

export function useComputePrequalPayment(
  params: ComputePrequalPaymentRequestDto | null,
): [ComputePrequalPaymentResultDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.prequalPayment(params),
    queryFn: getPrequalPayment,
    enabled: params !== null,
  })

  return [data ?? null, isFetching]
}
export type {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  MonthlyPayment,
  ComputePrequalPaymentRequestDto,
  ComputePrequalPaymentResultDto
}
